import { useState, type ReactNode } from 'react';
import { WAG_SUPPORT_EMAIL } from '@shared';
import { OnboardingBrandHeader, ProgressStep, type ProgressStepStatus } from '@/features/client-onboarding';
import { REVIEW_RESULTS_STEP_NAME } from '../lib/copy';
import { CONSUMER_STAGES, CONSUMER_STEPS, type ConsumerStepId } from '../lib/steps';
import { ConsumerFlowResetButton } from './consumer-flow-reset-button';
import { ConsumerFooterSlot } from './consumer-footer-slot';

interface ConsumerFlowLayoutProps {
  clientId: string;
  step: ConsumerStepId;
  children: ReactNode;
}

function stagePercentComplete(step: ConsumerStepId): number {
  const { stage } = CONSUMER_STEPS[step];
  const stageSteps = (Object.keys(CONSUMER_STEPS) as ConsumerStepId[]).filter(
    id => CONSUMER_STEPS[id].stage === stage,
  );
  return (stageSteps.indexOf(step) / stageSteps.length) * 100;
}

function stageStatus(index: number, currentStage: number): ProgressStepStatus {
  if (index < currentStage) return 'completed';
  if (index === currentStage) return 'current';
  return 'upcoming';
}

// Continues the onboarding progress bar: the results already reviewed, then the flow's stages.
function ConsumerProgress({ step }: { step: ConsumerStepId }) {
  const currentStage = CONSUMER_STEPS[step].stage;

  return (
    <nav aria-label="Progress" className="w-full">
      <ol className="flex space-x-2 md:space-x-8">
        <li className="flex-1">
          <div className="flex flex-col">
            <ProgressStep name={REVIEW_RESULTS_STEP_NAME} status="completed" />
          </div>
        </li>
        {CONSUMER_STAGES.map((stage, index) => (
          <li key={stage} className="flex-1" aria-current={index === currentStage ? 'step' : undefined}>
            <div className="flex flex-col">
              <ProgressStep
                name={stage}
                status={stageStatus(index, currentStage)}
                percentComplete={stagePercentComplete(step)}
              />
            </div>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function ConsumerFlowLayout({ clientId, step, children }: ConsumerFlowLayoutProps) {
  const [footerSlot, setFooterSlot] = useState<HTMLDivElement | null>(null);

  return (
    <ConsumerFooterSlot.Provider value={footerSlot}>
      <div className="bg-background relative flex h-dvh flex-col">
        <div className="z-30 shrink-0">
          <OnboardingBrandHeader
            progress={<ConsumerProgress step={step} />}
            extraActions={<ConsumerFlowResetButton clientId={clientId} />}
          />
        </div>

        <div className="flex flex-1 flex-col overflow-y-auto p-4 md:p-6">
          <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col gap-10 py-6 md:py-16">
            {children}
            <p className="text-muted-foreground text-xs">
              Estimates are not guarantees. Final policy terms and eligibility are set by the carrier.
              Questions? Email{' '}
              <a className="text-primary underline-offset-4 hover:underline" href={`mailto:${WAG_SUPPORT_EMAIL}`}>
                {WAG_SUPPORT_EMAIL}
              </a>
              .
            </p>
          </div>
        </div>

        <div ref={setFooterSlot} className="z-30 shrink-0" />
      </div>
    </ConsumerFooterSlot.Provider>
  );
}
